import { booleanValid, cleanCoords, kinks, multiPoint, polygon } from '@turf/turf';

const MINIMUM_DRAWN_VERTEX_COUNT = 3;
const MAXIMUM_DRAWN_VERTEX_COUNT = 99;

/**
 * Returns whether a map-editor coordinate is a finite WGS 84 latitude and longitude.
 * Turf validates polygon geometry, but it does not reject coordinates outside WGS 84 bounds.
 *
 * @param {{ lat?: number, lng?: number } | null | undefined} coordinate
 * @returns {boolean}
 */
const isValidWgs84Coordinate = (coordinate) =>
  Number.isFinite(coordinate?.lat) &&
  Number.isFinite(coordinate?.lng) &&
  coordinate.lat >= -90 &&
  coordinate.lat <= 90 &&
  coordinate.lng >= -180 &&
  coordinate.lng <= 180;

/**
 * Returns whether two map-editor coordinates represent the same point.
 *
 * @param {{ lat?: number, lng?: number } | null | undefined} firstCoordinate
 * @param {{ lat?: number, lng?: number } | null | undefined} secondCoordinate
 * @returns {boolean}
 */
const areCoordinatesEqual = (firstCoordinate, secondCoordinate) =>
  Boolean(
    firstCoordinate &&
    secondCoordinate &&
    firstCoordinate.lat === secondCoordinate.lat &&
    firstCoordinate.lng === secondCoordinate.lng,
  );

/**
 * Removes the optional closing coordinate stored by a map editor.
 * The returned array always contains only the vertices the operator drew.
 *
 * @param {Array<{ lat?: number, lng?: number }>} coordinates
 * @returns {Array<{ lat?: number, lng?: number }>}
 */
const getOpenPolygonCoordinates = (coordinates = []) => {
  if (!Array.isArray(coordinates)) return [];

  const lastCoordinate = coordinates.at(-1);
  return coordinates.length > 1 && areCoordinatesEqual(coordinates[0], lastCoordinate)
    ? coordinates.slice(0, -1)
    : coordinates;
};

/**
 * Creates the closed GeoJSON polygon that Turf needs for its geometry checks.
 * Call this only after the coordinate count and WGS 84 values have been checked.
 *
 * @param {Array<{ lat: number, lng: number }>} openCoordinates
 * @returns {import('geojson').Feature<import('geojson').Polygon>}
 */
const createTurfPolygon = (openCoordinates) => {
  // Leaflet and CAP name latitude first, while GeoJSON and Turf require longitude first.
  const turfPositions = openCoordinates.map(({ lat, lng }) => [lng, lat]);

  // GeoJSON linear rings must repeat the first position once at the end.
  return polygon([[...turfPositions, turfPositions[0]]]);
};

/**
 * Returns whether Turf finds the same drawn vertex more than once.
 *
 * @param {Array<{ lat: number, lng: number }>} openCoordinates
 * @returns {boolean}
 */
const hasDuplicateVertices = (openCoordinates) => {
  // MultiPoint cleanup removes exact repeats without changing the operator's polygon draft.
  const drawnPositions = openCoordinates.map(({ lat, lng }) => [lng, lat]);
  const uniquePositions = cleanCoords(multiPoint(drawnPositions)).geometry.coordinates;

  return uniquePositions.length < drawnPositions.length;
};

/**
 * Returns whether Turf can simplify the ring without collapsing it into a line.
 * A fully collinear ring becomes fewer than four closed positions, which Turf rejects.
 *
 * @param {import('geojson').Feature<import('geojson').Polygon>} polygonFeature
 * @returns {boolean}
 */
const hasEnclosedArea = (polygonFeature) => {
  try {
    cleanCoords(polygonFeature); // Turf requires polygons to have 3 unique positions and removes positions on the same line that are unnecessary.
    return true;
  } catch {
    return false;
  }
};

/**
 * Runs the shared IPAWS polygon checks and keeps the Turf feature for serialization.
 * CAP/IPAWS-specific input limits are checked first so Turf only receives safe GeoJSON data.
 *
 * @param {Array<{ lat?: number, lng?: number }>} coordinates
 * @returns {{ findings: string[], polygonFeature: import('geojson').Feature<import('geojson').Polygon> | null }}
 */
const analyzeIpawsPolygon = (coordinates = []) => {
  const findings = [];
  const openCoordinates = getOpenPolygonCoordinates(coordinates);
  const hasOnlyValidCoordinates = openCoordinates.every(isValidWgs84Coordinate);

  if (openCoordinates.length < MINIMUM_DRAWN_VERTEX_COUNT) {
    findings.push('Draw at least 3 polygon vertices. The closing point is added on submission.');
  }

  if (openCoordinates.length > MAXIMUM_DRAWN_VERTEX_COUNT) {
    findings.push(
      'Use no more than 99 drawn vertices so the closed polygon stays within 100 points.',
    );
  }

  if (!hasOnlyValidCoordinates) {
    findings.push('Every polygon coordinate must be a valid WGS 84 latitude and longitude.');
  }

  if (openCoordinates.length < MINIMUM_DRAWN_VERTEX_COUNT || !hasOnlyValidCoordinates) {
    return { findings, polygonFeature: null };
  }

  const polygonFeature = createTurfPolygon(openCoordinates);
  const containsDuplicateVertices = hasDuplicateVertices(openCoordinates);

  // Exact duplicates are reported separately because operators can fix that message directly.
  if (containsDuplicateVertices) {
    findings.push('Each drawn polygon vertex must be distinct.');
  }

  // Turf removes redundant points; a ring that then collapses cannot enclose an area.
  if (!containsDuplicateVertices && !hasEnclosedArea(polygonFeature)) {
    findings.push('Polygon vertices must enclose an area and cannot all be collinear.');
  }

  // Kinks finds crossings, while booleanValid also catches spikes and overlapping segments.
  if (!booleanValid(polygonFeature) || kinks(polygonFeature).features.length > 0) {
    findings.push('Polygon edges must not cross each other.');
  }

  return { findings, polygonFeature };
};

/**
 * Returns all frontend findings for an editable IPAWS polygon.
 * Turf performs the geometry checks; the backend remains authoritative for submission.
 *
 * @param {Array<{ lat?: number, lng?: number }>} coordinates
 * @returns {string[]}
 */
export const getIpawsPolygonFindings = (coordinates = []) =>
  analyzeIpawsPolygon(coordinates).findings;

/**
 * Serializes a valid editable polygon into CAP's WGS 84 `latitude,longitude` representation.
 * Turf supplies the validated, closed ring so the closing coordinate is included exactly once.
 *
 * @param {Array<{ lat?: number, lng?: number }>} coordinates
 * @returns {string}
 */
export const serializeIpawsPolygon = (coordinates = []) => {
  const { findings, polygonFeature } = analyzeIpawsPolygon(coordinates);
  if (findings.length || !polygonFeature) return '';

  // Convert GeoJSON's longitude-first positions back to CAP's latitude-first text format.
  return polygonFeature.geometry.coordinates[0]
    .map(([longitude, latitude]) => `${latitude},${longitude}`)
    .join(' ');
};
